import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { ActionLogEntry } from '@/admin/hooks/use-claims'

// ── Types ────────────────────────────────────────────────

export interface TransferReason {
  uuid: string
  kind: 'type' | 'approval' | 'denial' | 'auto_void'
  code: string
  label: string
  /**
   * Data-driven type semantics (only meaningful for kind='type'). Drive
   * the wizard's fee / documents steps and the record's fee / proof
   * sections without hardcoding type codes in the frontend.
   */
  counts_toward_usage?: boolean
  requires_fee?: boolean
  requires_documentation?: boolean
  /** False for a type that keeps the owner (e.g. a name change). */
  requires_eligibility?: boolean
}

export type TransferSource =
  | 'admin_ui'
  | 'email'
  | 'phone'
  | 'voice_ai'
  | 'consumer_portal'
  | 'chat'
  | 'public_api'

export interface EligibilityViolation {
  rule_uuid: string
  name: string
  severity: 'BLOCK' | 'WARN' | 'INFO'
  message: string
}

export interface ResolvedTransferPolicy {
  is_transferable: boolean
  fee_amount: string
  fee_currency: string
  standard_window_days: number | null
}

export interface EligibilityPreflightResult {
  passed: boolean
  requires_exception: boolean
  violations: EligibilityViolation[]
  resolved_policy: ResolvedTransferPolicy
}

export interface ProofOfOwnershipFile {
  uuid: string
  url: string | null
  mime_type: string
  file_type: string
  original_filename: string
  file_size: number
  created_at: string
}

export interface TransferRegistrationRow {
  uuid: string
  /** UUID of the source holding (the registration being transferred). */
  holding_uuid: string
  is_eligible: boolean
  void_reason_code: StructureVoidReasonCode
  void_reason_code_label: string
  void_reason_detail: string
  /**
   * UUID of the new Holding spawned for the buyer on sale-type
   * approvals - null while pending and for non-sale / auto-void rows.
   */
  created_holding_uuid: string | null
}

export interface InteractionNoteEntry {
  uuid: string
  body: string
  author_name: string | null
  author_email: string | null
  created_at: string
}

export interface Transfer {
  uuid: string
  scope?: 'structure' | 'host' | 'holding'
  structure_uuid?: string | null
  /**
   * Per-row registrations attached to this transfer. Source holding +
   * (post-approval) created holding back-link, plus the eligibility
   * decision recorded at initiation.
   */
  transfer_registrations?: TransferRegistrationRow[]
  /**
   * Append-only timestamped note log. Each call to the notes
   * endpoint creates a new row; existing rows are never edited or
   * deleted, so the audit trail stays intact.
   */
  interaction_notes_log?: InteractionNoteEntry[]
  status: string
  status_label: string
  transfer_type: TransferReason
  approval_reason?: TransferReason | null
  denial_reason?: TransferReason | null
  source: TransferSource
  channel_metadata: Record<string, unknown>
  eligibility_snapshot: EligibilityViolation[]
  new_owner_first_name: string
  new_owner_last_name: string
  new_owner_email: string | null
  new_owner_phone: string | null
  ownership_change_date: string | null
  first_contact_date: string | null
  fee_amount: string | null
  fee_waived: boolean
  fee_waived_reason: string
  fee_waived_by_email?: string | null
  payment_method: string
  payment_received: boolean
  payment_received_at: string | null
  payment_reference: string
  proof_of_ownership_verified: boolean
  proof_of_ownership_file?: ProofOfOwnershipFile | null
  /** A fee is due and not yet received; approval waits for it. */
  payment_outstanding?: boolean
  /** Claims on the transfer's registrations, open first. */
  linked_claims?: {
    uuid: string
    claim_number: string
    status_label: string
    is_open: boolean
  }[]
  /** The property's deed owner and last recorded sale, when the tenant has a
   *  property-data provider switched on. Reference only. */
  property_records?: {
    status: 'pending' | 'found' | 'not_found' | 'failed' | 'unsupported'
    deed_owner_full_name?: string
    deed_sale_date?: string
    checked_at?: string
    owner_match?: '' | 'new_owner' | 'previous_owner' | 'neither'
    sale_date_match?: '' | 'matches' | 'earlier' | 'later'
    outcome?: '' | 'match' | 'not_recorded' | 'mismatch'
  } | null
  /** Who the transfer moves the registrations from. */
  original_owner?: {
    first_name: string
    last_name: string
    email: string
    phone_number: string
  } | null
  reviewed_by_email?: string | null
  reviewed_at?: string | null
  interaction_notes: string
  override_reason?: string
  /** Capture from the 'request documents' action (rendered in the email). */
  document_request_message?: string
  document_request_cc?: string[]
  /** Override recipient captured on the 'request payment' action. */
  payment_request_email?: string
  /** Whether an automatic documentation reminder is active for this transfer. */
  auto_reminder_enabled?: boolean
  host_uuid: string
  host_address: import('@/shared/lib/formatting').Address | null
  holding_uuids: string[]
  created_at: string
  updated_at: string
  /**
   * True when the current user has an unread notification for this
   * transfer (drives the list-row attention dot). Populated by the
   * backend via the notification infrastructure.
   */
  has_unread_activity?: boolean
}

export interface PaginatedTransfers {
  count: number
  next: string | null
  previous: string | null
  results: Transfer[]
}

// Per-holding eligibility from GET /v1/transfers/structures/<uuid>/eligibility/
export type StructureVoidReasonCode =
  | ''
  | 'not_active'
  | 'not_transferable'
  | 'coverage_expired'
  | 'transfer_window_passed'
  | 'prior_transfer_limit'

export interface StructureEligibilityHolding {
  uuid: string
  is_eligible: boolean
  void_reason_code: StructureVoidReasonCode
  void_reason_detail: string
  warranty_label: string
  warranty_expires_at: string | null
  transfer_count: number
  max_transfers: number | null
  status: string
}

export interface StructureEligibilityResponse {
  structure: { uuid: string; label: string }
  host: {
    uuid: string
    address: import('@/shared/lib/formatting').Address | null
  } | null
  transfer_type: { code: string; label: string }
  current_owner: { first_name: string; last_name: string; email: string } | null
  /** An open transfer already covering this property's registrations. */
  open_transfer: {
    uuid: string
    status: string
    status_label: string
    new_owner_name: string
  } | null
  closing_date: string
  prior_transfer_count: number
  holdings: StructureEligibilityHolding[]
  summary: { total: number; eligible: number; ineligible: number }
}

export interface CreateTransferPayload {
  // Three input modes (mutually exclusive). Server derives `scope` from
  // whichever is supplied:
  //   structure_uuid → scope=structure (multi-building real estate)
  //   host_uuid      → scope=host (cars, yachts, single-building)
  //   holding_uuids  → scope=holding (no-host tenants)
  scope?: 'structure' | 'host' | 'holding'
  structure_uuid?: string
  host_uuid?: string
  holding_uuids?: string[]
  new_owner_first_name: string
  new_owner_last_name: string
  new_owner_email?: string
  new_owner_phone?: string
  // Optional alternative email for the new owner (e.g. a reviewer-edited
  // "email the new owner" address from the Documents step). When it
  // differs from new_owner_email the server pre-fills it as a second
  // contact under the same name.
  additional_owner_email?: string
  // §6: when the docs recipient is a distinct person (not the new owner),
  // their name is sent so the seeded contact is named correctly and made the
  // main contact.
  additional_owner_first_name?: string
  additional_owner_last_name?: string
  // §12/§14: "email the new owner" chosen in the Documents step - advance the
  // transfer to Awaiting Customer Information on create and start reminders.
  request_documents_on_create?: boolean
  // §10: the reviewer's edited note + Cc from the wizard "Email new owner" step,
  // so the documents-request email actually sent matches the draft on screen
  // (previously the draft was discarded). Only used when
  // request_documents_on_create is true.
  document_request_message?: string
  document_request_cc?: string[]
  setup_reminder?: boolean
  // A file uploaded in the wizard, attached as the proof of ownership on create.
  proof_of_ownership_file_uuid?: string
  ownership_change_date?: string
  // TransferReason code (kind='type') - e.g. 'sale'. Server resolves
  // it against the tenant's configured reasons.
  transfer_type: string
  source?: TransferSource
  channel_metadata?: Record<string, unknown>
  fee_waived?: boolean
  fee_waived_reason?: string
  payment_method: string
  interaction_notes: string
  // Set to true after the agent has reviewed and accepted a 409 response
  // carrying BLOCK-severity eligibility violations. Creates one
  // RuleException per block on success.
  override_accepted?: boolean
  // Free-text justification captured in the override modal (min 10
  // chars, enforced server-side when override_accepted=true). Persisted
  // on the transfer for approver audit.
  override_reason?: string
  // Generic workflow-engine binding: the instance UUID drives the shared
  // WorkflowActionMenu; workflow_history feeds the shared Activity timeline
  // (same rich ActionLogEntry shape as the claims record).
  workflow_instance_uuid?: string | null
  workflow_history?: ActionLogEntry[]
}

export interface EligibilityPreflightPayload {
  holding_uuid: string
  /** Every registration in the transfer, so the fee covers all of them. */
  holding_uuids?: string[]
  transfer_type: string
  ownership_change_date: string
}

// ── Hooks ────────────────────────────────────────────────

// Backend mounts transfers under /api/v1/transfers/ (see backend/config/urls.py).
// The `api` client already prepends /api, so paths here start with /v1/.
export function useTransfers(params?: Record<string, string>) {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<PaginatedTransfers>({
    queryKey: ['transfers', params],
    queryFn: () => api.get('/v1/transfers/', { params }).then(r => r.data),
    enabled,
  })
}

/**
 * The tenant's active transfer TYPE reasons, each with its data-driven
 * requires_fee / requires_documentation flags. Backs the wizard's
 * fee/documents step gating and the record's fee/proof section gating.
 */
export function useTransferTypes() {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferReason[]>({
    queryKey: ['transfer-types'],
    queryFn: () => api.get('/v1/transfers/transfer-types/').then((r) => r.data),
    enabled,
  })
}

/**
 * The tenant's active transfer reasons, all kinds in one fetch.
 *
 * Backs the approve / deny dialogs. These used to read a hardcoded frontend
 * constant authored for one tenant, so every other tenant saw that tenant's
 * reason vocabulary and a product owner could not add or rename a reason
 * without a frontend release.
 */
export function useTransferReasons() {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferReason[]>({
    queryKey: ['transfer-reasons'],
    queryFn: () => api.get('/v1/transfers/transfer-reasons/').then((r) => r.data),
    enabled,
  })
}

/** Approval and denial reasons, partitioned by the `kind` each row carries. */
export function useTransferDecisionReasons() {
  const query = useTransferReasons()
  const approval = (query.data ?? []).filter((r) => r.kind === 'approval')
  const denial = (query.data ?? []).filter((r) => r.kind === 'denial')
  return { ...query, approval, denial }
}

// ── Transfer documents (additional supporting documents) ─────────────

export interface TransferDocument {
  uuid: string
  document_type: string
  document_type_label: string
  label: string
  file: ProofOfOwnershipFile | null
  verified: boolean
  verified_by_email: string | null
  verified_at: string | null
  uploaded_by_email: string | null
  created_at: string
}

export function useTransferDocuments(uuid: string | null) {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferDocument[]>({
    queryKey: ['transfer-documents', uuid],
    queryFn: () => api.get(`/v1/transfers/${uuid}/documents/`).then((r) => r.data),
    enabled: enabled && !!uuid,
  })
}

export interface TransferDocChecklistItem {
  key: string
  label: string
  description: string
  required: boolean
}

/**
 * WPS-157 §10b: the document types a transfer can request from the customer,
 * from the resolved (tenant-configurable) evidence template - so the "request
 * additional documentation" dialog offers a real list, not a hardcoded array.
 */
export function useTransferDocumentChecklist(uuid: string | null, enabled = true) {
  const flagOn = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferDocChecklistItem[]>({
    queryKey: ['transfer-document-checklist', uuid],
    queryFn: () =>
      api
        .get(`/v1/transfers/${uuid}/document-checklist/`)
        .then((r) => r.data?.documents ?? []),
    enabled: flagOn && !!uuid && enabled,
  })
}

export function useUploadTransferDocument() {
  const qc = useQueryClient()
  return useMutation<
    TransferDocument,
    Error,
    { uuid: string; file: File; document_type?: string; label?: string }
  >({
    mutationFn: ({ uuid, file, document_type, label }) => {
      const form = new FormData()
      form.append('file', file)
      if (document_type) form.append('document_type', document_type)
      if (label) form.append('label', label)
      return api
        .post(`/v1/transfers/${uuid}/documents/`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data)
    },
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-documents', uuid] }),
  })
}

export function useVerifyTransferDocument() {
  const qc = useQueryClient()
  return useMutation<TransferDocument, Error, { uuid: string; docUuid: string }>({
    mutationFn: ({ uuid, docUuid }) =>
      api.post(`/v1/transfers/${uuid}/documents/${docUuid}/verify/`).then((r) => r.data),
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-documents', uuid] }),
  })
}

export function useDeleteTransferDocument() {
  const qc = useQueryClient()
  return useMutation<void, Error, { uuid: string; docUuid: string }>({
    mutationFn: ({ uuid, docUuid }) =>
      api.delete(`/v1/transfers/${uuid}/documents/${docUuid}/`).then((r) => r.data),
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-documents', uuid] }),
  })
}

// ── Transfer contacts ────────────────────────────────────────────────

export interface TransferContact {
  uuid: string
  user: string | null
  external_email: string
  external_name: string
  external_phone: string
  role_label: string
  permission: 'view_only' | 'collaborator'
  permission_label: string
  added_by: string | null
  added_by_name: string | null
  display_name: string
  display_email: string
  display_phone: string
  is_internal: boolean
  has_portal_access: boolean
  is_main_contact: boolean
  created_at: string
}

export interface TransferContactWrite {
  user?: string | null
  external_email?: string
  external_name?: string
  external_phone?: string
  role_label?: string
  permission?: 'view_only' | 'collaborator'
  is_main_contact?: boolean
}

export function useTransferContacts(uuid: string | null) {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferContact[]>({
    queryKey: ['transfer-contacts', uuid],
    queryFn: () => api.get(`/v1/transfers/${uuid}/contacts/`).then((r) => r.data),
    enabled: enabled && !!uuid,
  })
}

export function useAddTransferContact() {
  const qc = useQueryClient()
  return useMutation<TransferContact, Error, { uuid: string } & TransferContactWrite>({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/contacts/`, body).then((r) => r.data),
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-contacts', uuid] }),
  })
}

export function useUpdateTransferContact() {
  const qc = useQueryClient()
  return useMutation<
    TransferContact,
    Error,
    { uuid: string; contactUuid: string } & TransferContactWrite
  >({
    mutationFn: ({ uuid, contactUuid, ...body }) =>
      api
        .patch(`/v1/transfers/${uuid}/contacts/${contactUuid}/`, body)
        .then((r) => r.data),
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-contacts', uuid] }),
  })
}

export function useRemoveTransferContact() {
  const qc = useQueryClient()
  return useMutation<void, Error, { uuid: string; contactUuid: string }>({
    mutationFn: ({ uuid, contactUuid }) =>
      api.delete(`/v1/transfers/${uuid}/contacts/${contactUuid}/`).then((r) => r.data),
    onSuccess: (_data, { uuid }) =>
      qc.invalidateQueries({ queryKey: ['transfer-contacts', uuid] }),
  })
}

export interface TransferActivityEntry {
  uuid: string
  action: string
  action_label: string
  description: string
  actor_name: string | null
  actor_email: string | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  created_at: string
}

/**
 * Lifecycle activity log for a transfer (audit-trail backed), mirroring
 * the claims record's Activity timeline.
 */
export function useTransferActivity(uuid: string | null) {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferActivityEntry[]>({
    queryKey: ['transfer-activity', uuid],
    queryFn: () =>
      api.get(`/v1/transfers/${uuid}/activity/`).then((r) => r.data),
    enabled: enabled && !!uuid,
  })
}

export interface TransferStatusCount {
  key: string
  label: string
  count: number
}

/**
 * Global status-bucket counts across the full filtered dataset, backing
 * the list page's status pills (mirrors registrations'
 * ``useHoldingStatusCounts``). The counts endpoint ignores ``status`` so
 * clicking a pill filters the table without collapsing the pill bar.
 */
export function useTransferStatusCounts(params?: Record<string, string>) {
  const enabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<TransferStatusCount[]>({
    queryKey: ['transfer-status-counts', params],
    queryFn: () =>
      api.get('/v1/transfers/status-counts/', { params }).then((r) => r.data),
    enabled,
  })
}

export function useTransferDetail(uuid: string | null) {
  const transfersEnabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<Transfer>({
    queryKey: ['transfer', uuid],
    queryFn: () => api.get(`/v1/transfers/${uuid}/`).then(r => r.data),
    enabled: transfersEnabled && !!uuid,
    // Property records arrive a moment after the transfer is saved; check
    // back while they are pending, for two minutes at most.
    refetchInterval: (query) =>
      query.state.data?.property_records?.status === 'pending' &&
      query.state.dataUpdateCount < 40
        ? 3000
        : false,
  })
}

export function useCreateTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, CreateTransferPayload>({
    mutationFn: (data) => api.post('/v1/transfers/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transfers'] }),
  })
}

/**
 * Per-holding structure eligibility - backs the wizard's eligibility step.
 *
 * Returns ALL holdings on the structure (active + ineligible), each with
 * a chip-ready ``void_reason_code`` so the UI can render every row with
 * an "Eligible" or "Ineligible: <reason>" badge. Ineligible rows will be
 * voided on approval (server-driven, no client-side decision).
 *
 * Pass ``null`` for ``structureUuid`` to skip the request.
 */
export function useStructureEligibility(
  structureUuid: string | null,
  transferType: string,
  closingDate: string | null,
) {
  const transfersEnabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<StructureEligibilityResponse>({
    queryKey: ['structure-eligibility', structureUuid, transferType, closingDate],
    queryFn: () =>
      api
        .get(`/v1/transfers/structures/${structureUuid}/eligibility/`, {
          params: { transfer_type: transferType, closing_date: closingDate },
        })
        .then((r) => r.data),
    enabled:
      transfersEnabled && !!structureUuid && !!transferType && !!closingDate,
  })
}

/**
 * Per-holding HOST eligibility - same shape as ``useStructureEligibility``
 * but scoped to a Host (no structure FK). For tenants whose holdings
 * aren't linked to structures (cars, yachts, single-building properties,
 * or any holding that's host-only). The response shape is identical so
 * the wizard renders the same UI; ``structure`` is ``null``.
 */
export function useHostEligibility(
  hostUuid: string | null,
  transferType: string,
  closingDate: string | null,
) {
  const transfersEnabled = useFeatureFlag(FLAGS.TRANSFERS)
  return useQuery<StructureEligibilityResponse>({
    queryKey: ['host-eligibility', hostUuid, transferType, closingDate],
    queryFn: () =>
      api
        .get(`/v1/transfers/hosts/${hostUuid}/eligibility/`, {
          params: { transfer_type: transferType, closing_date: closingDate },
        })
        .then((r) => r.data),
    enabled:
      transfersEnabled && !!hostUuid && !!transferType && !!closingDate,
  })
}

/**
 * Runs the tenant's transfer-scoped ValidationRule set without
 * persisting a transfer. Returns the violation list + resolved policy
 * so the wizard can surface warnings and require an override for BLOCK
 * violations before the real submit.
 */
export function useEligibilityPreflight() {
  return useMutation<EligibilityPreflightResult, Error, EligibilityPreflightPayload>({
    mutationFn: (data) =>
      api.post('/v1/transfers/eligibility-preflight/', data).then(r => r.data),
  })
}

// ── Workflow action hooks ────────────────────────────────

// Which workflow actions show depends on the transfer's fields (fee due,
// proof verified), so every transfer write refreshes them too.
function invalidateTransferRecord(qc: QueryClient, uuid: string) {
  qc.invalidateQueries({ queryKey: ['transfer', uuid] })
  qc.invalidateQueries({ queryKey: ['transfers'] })
  qc.invalidateQueries({ queryKey: ['workflows', 'visible-actions'] })
  // Editing the new owner also updates their contact.
  qc.invalidateQueries({ queryKey: ['transfer-contacts', uuid] })
}

interface TransferActionPayload {
  uuid: string
}

export function useApproveTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & { approval_reason?: string }>(
    {
      mutationFn: ({ uuid, ...body }) =>
        api.post(`/v1/transfers/${uuid}/approve/`, body).then(r => r.data),
      onSuccess: (_data, { uuid }) => {
        invalidateTransferRecord(qc, uuid)
      },
    },
  )
}

export function useDenyTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & { denial_reason: string }>({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/deny/`, body).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useCancelTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload>({
    mutationFn: ({ uuid }) =>
      api.post(`/v1/transfers/${uuid}/cancel/`).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useHoldTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload>({
    mutationFn: ({ uuid }) => api.post(`/v1/transfers/${uuid}/hold/`).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useReleaseHoldTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload>({
    mutationFn: ({ uuid }) =>
      api.post(`/v1/transfers/${uuid}/release-hold/`).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useWaiveFee() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & { reason: string }>({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/waive-fee/`, body).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useUploadProofOfOwnership() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & { file: File }>({
    mutationFn: ({ uuid, file }) => {
      const form = new FormData()
      form.append('file', file)
      return api
        .post(`/v1/transfers/${uuid}/proof-of-ownership/`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(r => r.data)
    },
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export interface UpdateTransferPayload {
  first_contact_date?: string | null
  payment_method?: string
  payment_reference?: string
  fee_amount?: string | null
  new_owner_first_name?: string
  new_owner_last_name?: string
  new_owner_email?: string
  new_owner_phone?: string
}

/** PATCH editable transfer fields (first contact date, payment, fee). */
export function useUpdateTransfer() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & UpdateTransferPayload>({
    mutationFn: ({ uuid, ...body }) =>
      api.patch(`/v1/transfers/${uuid}/`, body).then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

/** Record that the transfer fee payment has been received. */
export function useRecordPaymentReceived() {
  const qc = useQueryClient()
  return useMutation<
    Transfer,
    Error,
    TransferActionPayload & { received_date?: string; payment_reference?: string }
  >({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/payment-received/`, body).then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useVerifyProofOfOwnership() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload>({
    mutationFn: ({ uuid }) =>
      api.post(`/v1/transfers/${uuid}/verify-proof/`).then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export interface RequestDocumentsPayload {
  message?: string
  cc_emails?: string[]
  setup_reminder?: boolean
}

export function useRequestDocuments() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & RequestDocumentsPayload>({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/request-documents/`, body).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}

export function useRequestPayment() {
  const qc = useQueryClient()
  return useMutation<Transfer, Error, TransferActionPayload & { recipient_email?: string }>({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/v1/transfers/${uuid}/request-payment/`, body).then(r => r.data),
    onSuccess: (_data, { uuid }) => {
      invalidateTransferRecord(qc, uuid)
    },
  })
}
